import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "api";
import { T_Replace } from "types/_utils.types";
import { BlogDto } from "dtos/blog.dto";
import { CustomerStoryDto, CustomerStorySchemaDto } from "dtos/customerStory.dto";
import { UserSchemaDto } from "dtos/user.dto";
import { T_Api_Paginated } from "./api.types";

type PopulatedBlog = T_Replace<BlogDto, "author", Pick<UserSchemaDto, "_id" | "name" | "email">>;

export const customerStoryService = {
  useGetAllPaginated: ({
    page,
    limit,
    categories = [],
    status = [],
    visibility = [],
    searchString = "",
    sortOrder = -1,
  }: {
    page: number;
    limit: number;
    categories?: string[];
    status?: string[];
    visibility?: string[];
    searchString: string;
    sortOrder?: number;
  }) => {
    return useQuery({
      queryKey: ["customer-stories/all", page, limit, categories, status, visibility, sortOrder],
      queryFn: async () => {
        const res = await api.get(
          `/story?page=${page}&limit=${limit}&status=${JSON.stringify(status)}&visibility=${JSON.stringify(visibility)}&categories=${JSON.stringify(categories)}&queryString=${searchString}&sortOrder=${sortOrder}`,
        );
        return res.data?.result as T_Api_Paginated<CustomerStorySchemaDto>;
      },
    });
  },

  useSearchBlogs: ({ page, limit, term }: { page: number; limit: number; term?: string }) => {
    return useQuery({
      queryKey: ["customer-stories/all", page, limit],
      queryFn: async () => {
        const res = await api.get(`/blog/search?queryString=${term}&page=${page}&limit=${limit}`);
        return res.data?.result as T_Api_Paginated<PopulatedBlog>;
      },
    });
  },

  useGetByIdOrSlug: (id: string | undefined) => {
    return useQuery({
      queryKey: ["story", id],
      queryFn: async () => {
        const res = await api.get(`/blog/${id}`);
        return res.data?.result as CustomerStorySchemaDto;
      },
      enabled: !!id,
    });
  },

  useCreateBlog: () => {
    return useMutation({
      mutationKey: ["story", "create"],
      mutationFn: async (body: CustomerStoryDto) => {
        const res = await api.post("/story", body);
        return res.data?.result as CustomerStoryDto;
      },
    });
  },
  useUpdateBySlug: () => {
    return useMutation({
      mutationKey: ["story/update"],
      mutationFn: async ({ slug, body }: { slug: string; body: CustomerStoryDto }) => {
        return api.patch(`/story/${slug}`, body);
      },
    });
  },
  useDeleteBySlugOrId: () => {
    return useMutation({
      mutationKey: ["blog/delete"],
      mutationFn: async (id: string) => {
        return api.delete(`/story/${id}`);
      },
    });
  },
  useGetBlogByCategory: (queries: string[]) => {
    return useQuery({
      queryKey: ["blog/category"],
      queryFn: async () => {
        const res = await api.get(`/blog/?categories=${queries}`);
        return res.data?.result as T_Api_Paginated<CustomerStorySchemaDto>;
      },
    });
  },
  // useGetAllMessageByBlogId,
  // useSendMessage,
};
